import React from 'react';
import { WEEK_SHAPE_PRESETS } from '../../constants/budget';
import WarningTriangleIcon from '../ui/WarningTriangleIcon';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * CrewDayGrid — the per-week worked-day control in the expanded crew-member
 * panel on `pages/BudgetCostCodes.js`. Task 6 of the 6th/7th-day premium wage
 * feature.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Replaces the per-week allocation `<select>` (0 / 0.2 / … / 1.0) that used
 * to sit in these chips. Same chips, same wrapping row, same three tints — a
 * different value inside.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * 🔑 ONE CONTROL PER WEEK, DAYS ON DEMAND — owner-confirmed 2026-08-21:
 *    "yes thats what I pictured, per week preset with expand for days."
 * ─────────────────────────────────────────────────────────────────────────
 * 🚨 DO NOT "SIMPLIFY" THIS INTO SEVEN ALWAYS-VISIBLE TICK BOXES. The chips
 * wrap, so there is no horizontal-space problem — the problem is TAB STOPS.
 * `weeks.map` renders every week: there is no virtualisation, no pagination
 * and no roving-tabindex pattern anywhere in this codebase to copy. Seven
 * ticks per week is 7 stops per week; a 6-month engagement is 26 weeks (182)
 * and the ruled 2-year cap is 104 weeks (**728 stops**).
 *
 * 📏 MEASURED, so the claim is checkable rather than asserted. Per chip this
 * component renders TWO focusable elements — the preset `<select>` and the
 * expand toggle on the week label — so the 2-year worst case is **208 stops,
 * plus 7 for the single expanded week = 215**, against 728. Only ONE week can
 * be expanded at a time (`expandedWeek` is a single value, not a Set), and
 * that is what keeps the +7 a constant rather than a multiplier.
 * ⚠️ The expand toggle is a REAL BUTTON and must stay one. Hiding it behind
 * hover, or giving it `tabIndex={-1}`, would take the count to 104 and take
 * "tick a specific day" away from every keyboard user — which is the group
 * the tab-stop budget exists to protect in the first place.
 *
 * 📏 A chip also grows ~60px -> ~150px once its ticks are visible: on a 375px
 * phone that is 5 chips per row down to 2. Another reason only one expands.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * 🪤 `InfoTip` MUST NEVER GO INSIDE A `<label>` THAT WRAPS A CHECKBOX
 * ─────────────────────────────────────────────────────────────────────────
 * `components/InfoTip.js`'s outer wrapper is
 * `<span className="relative inline-flex items-center group align-middle">`
 * — it is NOT `pointer-events-none` (only the bubble inside it is). A click
 * on the ⓘ therefore counts as a click on the enclosing `<label>` and TOGGLES
 * THE CHECKBOX. A tick grid with per-day help is exactly the shape that walks
 * into it. Any explanatory affordance here lives OUTSIDE the label — this
 * component uses the `InactiveBadge.js` device instead (native `title` + an
 * `sr-only` span fed from ONE exported string), which needs no wrapper at all.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * 🚫 `opacity-*` IS A TAKEN IDIOM ON THIS PAGE
 * ─────────────────────────────────────────────────────────────────────────
 * `opacity-40 pointer-events-none` on `BudgetCostCodes.js` means "another row
 * has unsaved changes", and `InactiveBadge.js` forbids reusing it for
 * anything else. So an OFF week is ghosted with a **dashed border**, which is
 * already the Gantt's own vocabulary for an off week, never with opacity.
 */

/* ───────────────────────────── Day vocabulary ─────────────────────────────
 * Index 0 is MONDAY. That is not a preference: `generateWeeks` keys every
 * week on its Monday, `enumerateCrewWeeks` snaps to the Monday on/before
 * `start_date`, and the owner's ruling is "Monday is day one until a buyer or
 * supervisor overrides". A Sunday-first array here would silently offset
 * every `work_date` this grid writes. */
export const DAY_INITIALS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
export const DAY_NAMES = [
  'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday',
];

/**
 * The day set a preset fills, MONDAY-FIRST.
 *   6-day week -> Mon–Sat.   7-day week -> Mon–Sun.   1–5 -> Mon onwards.
 * Owner: "Selecting a 6 day week will by default highlight monday through to
 * saturday. But the buyer/supervisor can select any number of days."
 * The fill is a DEFAULT, never a rule — `onDayToggle` may then move any of it.
 */
export function daysForPreset(dayCount) {
  const n = Math.max(0, Math.min(7, Math.round(Number(dayCount) || 0)));
  return Array.from({ length: n }, (_, i) => i);
}

/**
 * The preset a day set reads as. THE PRESET AND THE TICKS ARE ONE STATE:
 * ticking Saturday on a 5-day week makes the control read "6-day week",
 * because six days ARE a six-day week however they are arranged. There is no
 * separate "custom" value to get out of sync with the ticks.
 * 🪤 Mon+Tue+Sat is therefore "3 days" even though it is not Mon–Wed. That is
 * correct and deliberate: the preset names the COUNT (which is what drives
 * the allocation and therefore the money), the ticks name the arrangement
 * (which is what drives the consecutive-run position at Stage 3).
 */
export function presetForDays(dayIndices) {
  return Array.isArray(dayIndices) ? dayIndices.length : 0;
}

/** 'YYYY-MM-DD' for day `offset` after the Monday `weekStart`. Local, no UTC. */
export function dateForWeekDay(weekStart, offset) {
  const d = new Date(`${weekStart}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  d.setDate(d.getDate() + offset);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/* ══════════════════════ THE UNPRICED-PREMIUM WARNING ══════════════════════
 *
 * A 6th or 7th day whose grade carries no premium rate is ALLOWED (owner
 * ruling): the day still counts as run position 6/7 and costs the STANDARD
 * rate until a premium exists. Ticking it is never blocked. It WARNS.
 *
 * 🚨 THIS MARKER IS THE SOLE CARRIER OF "this premium is unpriced", so the
 * root CLAUDE.md sole-carrier rule applies in full — WCAG 1.4.11, 3:1
 * MEASURED against the cell background, plus a NON-COLOUR channel.
 *
 * ── WHY A TRIANGLE AND NOT A DOT ─────────────────────────────────────
 * A triangle is a SHAPE and carries its meaning without colour. A coloured
 * dot does not, and a dot has already failed this exact test on this page
 * (`bg-amber-500` measured 2.15:1 on white and became amber-600).
 *
 * ── THE MEASUREMENTS, AND WHY amber-600 WAS NOT ENOUGH ───────────────
 * 🔴 The brief for this task said "the house answer is amber-600 light /
 * amber-400 dark". **amber-600 FAILS on two of the four light backgrounds
 * this marker actually sits on**, so it is not used here. Measured (WCAG 2.x
 * relative luminance) against every background, in both themes:
 *
 *   LIGHT — text-amber-700 (#b45309)          amber-600 (#d97706) would be
 *     panel        bg-gray-50  #f9fafb  4.81:1              3.05:1
 *     chip default bg-white    #ffffff  5.02:1              3.19:1
 *     chip override bg-blue-50 #eff6ff  4.61:1         🔴   2.93:1  FAILS
 *     chip unsaved bg-amber-100 #fef3c7  4.51:1        🔴   2.86:1  FAILS
 *
 *   DARK — dark:text-amber-400 (#fbbf24). 🪤 The backgrounds are NOT the
 *   `dark:` classes on the elements: `client/src/index.css` carries
 *   `!important` overrides that beat them outright, so measure the CASCADE,
 *   not the class list.
 *     panel        `bg-gray-50` -> !important #1e293b            8.76:1
 *     chip default `bg-white`   -> !important #1e293b            8.76:1
 *       (`dark:bg-gray-800` on that chip is INERT — the !important wins)
 *     chip override `bg-blue-50` -> !important rgba(59,130,246,.1)
 *                                   composited = #21324e         7.71:1
 *     chip unsaved  no override, so `dark:bg-amber-900/30`
 *                                   composited = #392d2e         7.92:1
 *
 * Worst case 4.51:1 light / 7.71:1 dark — both clear the 3:1 non-text floor
 * with margin, and in fact clear the 4.5:1 body-text floor too.
 * 🔑 `text-amber-700 dark:text-amber-400` is the SAME pair `InactiveBadge.js`
 * uses for the same reason. Reusing it is deliberate: one amber that means
 * "caution, nothing is broken" is easier to keep measured than two.
 * 🚫 Never express this as `opacity-*` — see the header block.
 *
 * ── DISTINGUISHING IT FROM THE OTHER TRIANGLE IN THE SAME ROW ────────
 * ⚠️ `UserStatusIndicator` (`components/UserAutocomplete.js`) already draws a
 * warning triangle on a crew row: `text-yellow-500 dark:text-yellow-400`,
 * `title`-only, no `sr-only`, and it means "placeholder user". The GLYPH is
 * reused here (one triangle path in the codebase, not two that drift); the
 * WRAPPER is not. Three things separate them for a reader:
 *   1. LOCATION — that one is in the collapsed row's NAME cell; this one is
 *      only ever inside the expanded panel's week chips and day ticks.
 *   2. COLOUR — amber-700 is materially darker and browner than yellow-500,
 *      and they are never adjacent.
 *   3. ANNOUNCEMENT — this one carries an `sr-only` sentence naming WHICH
 *      rate is missing and WHERE to set it; that one is silent to a screen
 *      reader. A user who cannot see either colour still gets the difference.
 * The glyph itself is the shared `ui/WarningTriangleIcon`.
 */

/**
 * The ONE wording. The `title` and the `sr-only` copy are both fed from this
 * function — the `InactiveBadge.js` device — so the tooltip and the screen
 * reader can never drift apart. It names WHICH day rate is affected and the
 * remedy the owner ruled on, both requirements rather than politeness.
 *
 * 🔻 REWORDED 2026-08-24, OWNER RULING — the tooltip must name the WEEKLY
 * rate. Since the 1.5×/2× default landed, UNPRICED is a TWO-TERM state
 * (`resolvePremiumRates`, server side): nobody stated a premium rate on
 * EITHER rung **and** the member has no usable weekly rate to take a default
 * from. The old copy — "No 6th-day rate is set for this crew grade … paid at
 * the standard rate … Set the premium rates under Crew Grades" — was wrong
 * twice for that state: the "standard rate" is £0 for exactly the reason the
 * default is unavailable, and the missing weekly rate, not Crew Grades'
 * premium column, is the root cause the buyer needs pointing at.
 * ⚠️ Stating a positive premium rate on either rung WOULD also clear the
 * state (a stated rate always prices the day — crewCost.js's `sixthStated`).
 * The owner chose to point at the weekly rate, so the copy names that one
 * remedy; it deliberately never claims the other cannot work.
 */
export function unpricedPremiumTitle(positions) {
  const list = Array.isArray(positions) ? positions : [positions];
  const has6 = list.includes(6);
  const has7 = list.includes(7);
  const which = has6 && has7
    ? '6th-day and 7th-day rates cannot'
    : has7
      ? '7th-day rate cannot'
      : '6th-day rate cannot';
  return `This member has no weekly rate, so their ${which} be worked out. `
    + 'Set a weekly rate on their grade or as an override.';
}

/**
 * Exported so tests assert the string the component renders, not a copy.
 *
 * ⚠️ THE NAME IS NOW NARROWER THAN THE MEANING, and is kept deliberately.
 * Since 2026-08-23 this amber pair carries BOTH premium warnings — the
 * unpriced one and the new below-the-usual-uplift one — because the owner
 * asked for the same triangle in the same colour with a different sentence.
 * Renaming it would churn the four test files and the topic file that quote
 * it, and every MEASURED contrast figure attached to it (the four-row table
 * in the block above) is a fact about this colour pair on those backgrounds
 * and is unaffected by what the mark now means.
 */
export const UNPRICED_PREMIUM_CLASS = 'text-amber-700 dark:text-amber-400';

/**
 * THE MULTIPLIERS, AS COPY. The interface has to say what "the usual uplift"
 * is or the warning is unactionable, and the owner's own words are
 * *"less than 1.5x"* / *"2x daily rate"*.
 *
 * 🪞 A MIRROR OF `SIXTH_DAY_DEFAULT_MULTIPLIER` / `SEVENTH_DAY_DEFAULT_MULTIPLIER`
 * in `server/helpers/crewCost.js`, and a mirror is the ONE thing this
 * directory is allowed instead of a re-derivation: **nothing here multiplies
 * by these**, they only appear inside a sentence. 🔀 2026-10-25 (owner ruling,
 * display only): the PAGE does multiply — `pages/BudgetCostCodes.js`'s
 * `premiumPlaceholder` / `overtimeMultiplierFigure` print what a blank box
 * comes to, off its OWN mirrors (`PREMIUM_DEFAULT_MULTIPLIERS`,
 * `OVERTIME_MULTIPLIER_DEFAULTS`, pinned to the server as text by
 * `BudgetCostCodes.boxFigures.test.js` §1); the server still prices. Held in step by
 * `server/tests/regressions/premium-rate-defaults.test.js` §5, which reads
 * this file as TEXT across the package boundary (the `slack-bands` device),
 * so changing the server's uplift without changing this copy fails CI.
 */
export const PREMIUM_DEFAULT_MULTIPLIER_LABELS = { 6: '1.5×', 7: '2×' };

/**
 * The wording for a rate somebody HAS set that is below the usual uplift.
 *
 * 🔴 ADVISORY, NEVER AN ERROR. The owner's phrasing is *"a warning that it
 * might not be set correctly"*, and a production may legitimately pay less
 * than the usual uplift — a rate below it is a thing to glance at, not a
 * thing that is wrong. Do not write copy that says the rate is incorrect.
 *
 * 🔻 IT NAMES THE RUNG SINCE 2026-08-24, OWNER RULING. `*_below_default` is
 * computed server-side from the STATED rate, which comes from EITHER rung —
 * the member's own `*_day_rate_override` first, else the grade
 * (`crewCost.js`: "STATED MEANS EITHER RUNG"). Always saying "set for this
 * crew grade … check it under Crew Grades" sent the buyer of an OVERRIDDEN
 * member to a screen where the number is fine; they found nothing wrong and
 * gave up. `overridePositions` (which of 6/7 got their stated rate from the
 * member override) is handed in ALREADY DECIDED, from the server-published
 * `*_day_rate_is_override` — this function compares no rates and resolves no
 * override itself. Omitted/empty, the output is byte-identical to the
 * pre-ruling grade wording, which is the safe fallback for a stale payload.
 */
export function belowDefaultPremiumTitle(positions, overridePositions, rates) {
  const list = Array.isArray(positions) ? positions : [positions];
  const ov = overridePositions || new Set();
  const has6 = list.includes(6);
  const has7 = list.includes(7);
  // 🔀 STAGE 3 (owner 2026-10-28): "the usual uplift" is the PRODUCTION's — `rates` is the member's
  // server-published `agreement_rates`. Absent (an older payload): the Pact mirror above. Copy only.
  const label = (pos, key) => (rates && Number.isFinite(rates[key]) ? `${rates[key]}×` : PREMIUM_DEFAULT_MULTIPLIER_LABELS[pos]);
  const L = { 6: label(6, 'sixth_day_multiplier'), 7: label(7, 'seventh_day_multiplier') };
  const ov6 = has6 && ov.has(6);
  const ov7 = has7 && ov.has(7);
  if (has6 && has7 && ov6 !== ov7) {
    // Mixed rungs — one rate from the grade, the other from the member's
    // override. Each clause names its own rung; the remedy names both.
    const rung = (isOverride) => (isOverride ? 'this member' : 'this crew grade');
    return `The 6th-day rate set for ${rung(ov6)} and the 7th-day rate set for ${rung(ov7)} `
      + `are below the usual ${L[6]} and ${L[7]} of the daily rate. `
      + 'That may be intended — check them under Crew Grades and in their rate override.';
  }
  if (ov6 || ov7) {
    const which = has6 && has7
      ? `The 6th-day and 7th-day rates set for this member are below the usual ${L[6]} and ${L[7]} of the daily rate`
      : has7
        ? `The 7th-day rate set for this member is below the usual ${L[7]} the daily rate`
        : `The 6th-day rate set for this member is below the usual ${L[6]} the daily rate`;
    return `${which}. That may be intended — check their rate override${has6 && has7 ? 's' : ''}.`;
  }
  const which = has6 && has7
    ? `The 6th-day and 7th-day rates set for this crew grade are below the usual ${L[6]} and ${L[7]} of the daily rate`
    : has7
      ? `The 7th-day rate set for this crew grade is below the usual ${L[7]} the daily rate`
      : `The 6th-day rate set for this crew grade is below the usual ${L[6]} the daily rate`;
  return `${which}. That may be intended — check it under Crew Grades.`;
}

/**
 * The shared badge behind both premium warnings: ONE triangle path, ONE amber
 * pair, ONE string feeding both the `title` and the `sr-only` span.
 * Code Unification Principle — the two warnings differ only in their sentence
 * and their testid, so only those are parameters.
 */
function PremiumWarningBadge({ title, testId, className }) {
  return (
    <span
      title={title}
      data-testid={testId}
      className={`inline-flex ${UNPRICED_PREMIUM_CLASS} ${className}`.trim()}
    >
      <WarningTriangleIcon className="w-3 h-3" />
      {/* Not `aria-label` on the span: `title` on a bare span with no widget
          role is announced inconsistently, so it cannot be the only non-visual
          channel. `sr-only` is announced everywhere. */}
      <span className="sr-only">{title}</span>
    </span>
  );
}

export function UnpricedPremiumWarning({ positions, className = '' }) {
  return (
    <PremiumWarningBadge
      title={unpricedPremiumTitle(positions)}
      testId="unpriced-premium-warning"
      className={className}
    />
  );
}

/**
 * 🔴 A SECOND COMPONENT, NOT A SECOND STATE ON THE FIRST — decided, not
 * drifted into. The two warnings are about different facts ("nobody set a
 * rate" vs "somebody set a low one"), they can BOTH be true on one week chip
 * (a 6th day unpriced and a 7th below the uplift), and every existing
 * assertion about `unpriced-premium-warning` keeps meaning exactly what it
 * meant. A test can also say WHICH warning it is looking at, which a single
 * component with a mode prop makes awkward.
 */
export function BelowDefaultPremiumWarning({ positions, overridePositions, rates, className = '' }) {
  return (
    <PremiumWarningBadge
      title={belowDefaultPremiumTitle(positions, overridePositions, rates)}
      testId="below-default-premium-warning"
      className={className}
    />
  );
}

/** The wording for a week whose days are the boot backfill's ASSUMPTION. */
export const PRESUMED_WEEK_TITLE =
  'Assumed Monday-first — nobody has recorded which days were worked. '
  + 'Saving this member records them.';

/* ═══════════════════════════════ The grid ════════════════════════════════ */

/**
 * Props
 *  weeks             string[]  Monday keys, in order (from `generateWeeks`)
 *  weekDays          { [weekStart]: number[] }  LOCAL, editable day indices
 *  serverWeekDays    { [weekStart]: number[] }  last-saved, for the tint
 *  presumedWeeks     Set<string>  weeks whose days are all `is_presumed = 1`
 *  weekOverrides     { [weekStart]: number }  LOCAL allocation map
 *  serverOverrides   { [weekStart]: number }  saved allocation map
 *  defaultAlloc      number
 *  dragWeekDays      { [weekStart]: number }  drag-implied DAY COUNTS
 *  runPositions      Map<'YYYY-MM-DD', number>
 *  unpricedPositions Set<number>  which of 6/7 have no rate AT ALL
 *  belowDefaultPositions Set<number>  which of 6/7 have a STATED rate that is
 *                    below the usual uplift. 🔑 Handed in ALREADY DECIDED, by
 *                    the page, from the server-published
 *                    `sixth_day_rate_below_default` — this component compares
 *                    no rate against any threshold, exactly as it resolves no
 *                    rate for `unpricedPositions`. §5 of `CrewDayGrid.test.js`
 *                    pins that it reads no rate field at all.
 *  overridePositions Set<number>  which of 6/7 got their STATED rate from the
 *                    MEMBER's rate override rather than the grade — again
 *                    handed in ALREADY DECIDED, from the server-published
 *                    `*_day_rate_is_override`. Read only to word the
 *                    below-default warning's rung; never resolved here.
 *  agreementRates    the member's server-published `agreement_rates` (2026-10-28,
 *                    agreement Stage 3) — only to NAME the production's uplift in
 *                    the below-default sentence; never compared, never multiplied.
 *  canEdit           boolean
 *  expandedWeek      string | null
 *  onToggleWeek      (weekStart) => void
 *  onPresetChange    (weekStart, dayCount) => void
 *  onDayToggle       (weekStart, dayIndex) => void
 *  formatWeekLabel   (weekStart) => string
 */
export default function CrewDayGrid({
  weeks,
  weekDays,
  serverWeekDays,
  presumedWeeks,
  weekOverrides,
  serverOverrides,
  defaultAlloc,
  dragWeekDays,
  runPositions,
  unpricedPositions,
  belowDefaultPositions,
  overridePositions,
  agreementRates,
  canEdit,
  expandedWeek,
  onToggleWeek,
  onPresetChange,
  onDayToggle,
  formatWeekLabel,
}) {
  const presumed = presumedWeeks || new Set();
  const unpriced = unpricedPositions || new Set();
  const belowDefault = belowDefaultPositions || new Set();
  const overrides = overridePositions || new Set();
  const positions = runPositions || new Map();

  /**
   * Premium positions in this week that are in `flagged`. Sorted, deduped.
   * ONE walk shape for both warnings — the second was going to be a copy of
   * the first, which is how two markers start disagreeing about which day
   * they are describing.
   */
  const flaggedInWeek = (weekStart, dayIndices, flagged) => {
    const found = new Set();
    for (const idx of dayIndices) {
      const date = dateForWeekDay(weekStart, idx);
      const pos = date ? positions.get(date) : undefined;
      if ((pos === 6 || pos === 7) && flagged.has(pos)) found.add(pos);
    }
    return [...found].sort();
  };

  return (
    <div>
      <div className="flex flex-wrap gap-1 pb-2" data-testid="crew-day-grid">
        {weeks.map((ws) => {
          const isWeekExpanded = expandedWeek === ws;
          const dragCount = dragWeekDays ? dragWeekDays[ws] : undefined;
          const localDays = weekDays[ws] || [];
          // A Gantt drag PREVIEWS a day count it has not saved; it wins over
          // local state for display only, exactly as the allocation preview
          // did before this change.
          const days = dragCount !== undefined ? daysForPreset(dragCount) : localDays;
          const dayCount = presetForDays(days);
          const isOff = dayCount === 0;

          const savedDays = (serverWeekDays && serverWeekDays[ws]) || [];
          const daysChanged = dragCount === undefined
            && JSON.stringify([...localDays].sort((a, b) => a - b))
               !== JSON.stringify([...savedDays].sort((a, b) => a - b));
          const savedAlloc = serverOverrides[ws] !== undefined ? serverOverrides[ws] : defaultAlloc;
          const allocChanged = weekOverrides[ws] !== undefined && weekOverrides[ws] !== savedAlloc;
          const isLocallyChanged = daysChanged || allocChanged;
          const isOverridden = serverOverrides[ws] !== undefined && serverOverrides[ws] !== defaultAlloc;

          const weekUnpriced = flaggedInWeek(ws, days, unpriced);
          const weekBelowDefault = flaggedInWeek(ws, days, belowDefault);
          const isPresumed = presumed.has(ws) && !isLocallyChanged;

          return (
            <div
              key={ws}
              data-testid={`crew-week-chip-${ws}`}
              data-week-off={isOff ? 'true' : undefined}
              data-week-presumed={isPresumed ? 'true' : undefined}
              className={`flex flex-col items-center gap-1 px-1.5 py-2 rounded text-xs ${
                isWeekExpanded ? 'min-w-[150px]' : 'min-w-[60px]'
              } ${isOff ? 'border-dashed' : ''} ${
                isLocallyChanged
                  ? 'bg-amber-100 dark:bg-amber-900/30 border border-amber-300 dark:border-amber-600'
                  : isOverridden
                    ? 'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700'
                    : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600'
              }`}
            >
              {/* The week label doubles as the expand toggle. One extra tab
                  stop per week rather than seven — see the header block. */}
              <button
                type="button"
                onClick={() => onToggleWeek(ws)}
                aria-expanded={isWeekExpanded}
                data-testid={`crew-week-toggle-${ws}`}
                title={isPresumed ? PRESUMED_WEEK_TITLE : undefined}
                className={`inline-flex items-center gap-1 font-medium text-gray-600 dark:text-gray-400 whitespace-nowrap rounded px-0.5 hover:text-blue-600 dark:hover:text-blue-400 focus-visible:ring-2 focus-visible:ring-indigo-500 ${
                  isPresumed ? 'underline decoration-dotted decoration-gray-400 dark:decoration-gray-500 underline-offset-2' : ''
                }`}
              >
                <span>{formatWeekLabel(ws)}</span>
                {isPresumed && <span className="sr-only">{PRESUMED_WEEK_TITLE}</span>}
                {weekUnpriced.length > 0 && <UnpricedPremiumWarning positions={weekUnpriced} />}
                {weekBelowDefault.length > 0 && (
                  <BelowDefaultPremiumWarning positions={weekBelowDefault} overridePositions={overrides} rates={agreementRates} />
                )}
                <span aria-hidden="true" className="text-[9px] leading-none">
                  {isWeekExpanded ? '▲' : '▼'}
                </span>
              </button>

              {canEdit ? (
                <select
                  value={dayCount}
                  aria-label={`Days worked, week beginning ${formatWeekLabel(ws)}`}
                  onChange={(e) => onPresetChange(ws, parseInt(e.target.value, 10))}
                  data-testid={`crew-week-preset-${ws}`}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded px-1 py-0.5 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 text-xs text-center"
                >
                  {WEEK_SHAPE_PRESETS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              ) : (
                <span className="text-gray-800 dark:text-gray-200 font-medium" data-testid={`crew-week-readonly-${ws}`}>
                  {WEEK_SHAPE_PRESETS.find((o) => o.value === dayCount)?.label ?? dayCount}
                </span>
              )}

              {isWeekExpanded && (
                <div className="flex gap-0.5 pt-1" data-testid={`crew-week-days-${ws}`}>
                  {DAY_INITIALS.map((initial, idx) => {
                    const date = dateForWeekDay(ws, idx);
                    const checked = days.includes(idx);
                    const pos = date ? positions.get(date) : undefined;
                    const isPremiumDay = checked && (pos === 6 || pos === 7);
                    const showWarn = isPremiumDay && unpriced.has(pos);
                    const showBelowWarn = isPremiumDay && belowDefault.has(pos);
                    // 🪤 NO InfoTip inside this label — see the header block.
                    return (
                      <label
                        key={idx}
                        data-testid={`crew-day-${ws}-${idx}`}
                        className={`flex flex-col items-center justify-center min-h-[44px] md:min-h-0 w-5 rounded ${
                          canEdit ? 'cursor-pointer' : 'cursor-default'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={!canEdit}
                          onChange={() => onDayToggle(ws, idx)}
                          aria-label={`${DAY_NAMES[idx]} ${date || ''}`}
                          className="w-3 h-3"
                        />
                        <span aria-hidden="true" className="text-[10px] text-gray-600 dark:text-gray-400">
                          {initial}
                        </span>
                        {showWarn && <UnpricedPremiumWarning positions={[pos]} />}
                        {showBelowWarn && <BelowDefaultPremiumWarning positions={[pos]} overridePositions={overrides} rates={agreementRates} />}
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center gap-4 mt-2 text-xs text-gray-500 dark:text-gray-400">
        <span className="inline-flex items-center gap-1">
          <span className="inline-block w-3 h-3 rounded border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800"></span>
          Default
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="inline-block w-3 h-3 rounded border border-blue-200 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/20"></span>
          Saved override
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="inline-block w-3 h-3 rounded border border-amber-300 dark:border-amber-600 bg-amber-100 dark:bg-amber-900/30"></span>
          Unsaved change
        </span>
        <span className="inline-flex items-center gap-1">
          {/* 🔑 THE SWATCH IS THE CHIP, PLUS `border-dashed` — nothing else.
              A real off week is the ordinary chip tint with a dashed border
              (`border-gray-200 dark:border-gray-600` + `bg-white
              dark:bg-gray-800`, see the chip above); this swatch was
              `border-gray-400 dark:border-gray-500` and read two steps DARKER
              than the thing it describes. That is the identical defect just
              corrected in the Gantt's legend — a legend that is heavier than
              reality teaches the wrong weight — and it was reintroduced two
              files away. Any change to the chip's default tint belongs here
              in the same edit; pinned by `CrewDayGrid.test.js` §7. */}
          <span data-testid="crew-day-legend-off" className="inline-block w-3 h-3 rounded border border-dashed border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800"></span>
          0 days
        </span>
      </div>
    </div>
  );
}
